import invariant from 'invariant';

import { nonNull } from '@mythos/utils/utils';
import { match } from 'ts-pattern';
import {
  CardAffinity,
  CardType,
  affinityCharToCardAffinity,
  getAllCardChars,
  getCardAffinityChar,
  getCardTypeChar,
  isCardAffinity,
  isCardType,
  maybeCharToCardAffinity,
  maybeCharToCardType,
  typeCharToCardType,
} from './CardTypes';
import { CardDef } from './GameModel';
import { CharacterToResourceName, Resource, Token } from './Resources';
import { Context } from './Rules';
import * as Utility from './Utility';

export abstract class BaseCardAbility {
  private _outputs: AbilityOutput[];
  constructor(outputString: string) {
    this._outputs = parseAbilityOutputs(outputString);
  }

  public get outputs(): AbilityOutput[] {
    return this._outputs;
  }
}
export class GainSelfAbility extends BaseCardAbility {
  public onGainSelf(context: Context): Utility.CounterDelta {
    return computeAbilityOutputs(this.outputs, context);
  }
}
export class EachTurnAbility extends BaseCardAbility {
  public onTurn(context: Context): Utility.CounterDelta {
    return computeAbilityOutputs(this.outputs, context);
  }
}
export class BaseCountersAbility extends BaseCardAbility {
  public getBaseCounters(context: Context): Utility.CounterDelta {
    return computeAbilityOutputs(this.outputs, context);
  }
}

export class EndOfGameAbility extends BaseCardAbility {
  public onEndOfGame(context: Context): Utility.CounterDelta {
    return computeAbilityOutputs(this.outputs, context);
  }
}

// XXX(mythos3): draft left/right
export type DraftTarget = CardAffinity;
export class DraftCardAbility extends BaseCardAbility {
  private _draftTarget: DraftTarget;

  constructor(effectString: string) {
    const pattern = /\+([DTZ])(.*)/;
    const match = pattern.exec(effectString);
    invariant(match, 'invalid draft card ability: %s', effectString);
    const cardChar = match[1];
    const cardType = affinityCharToCardAffinity(cardChar);
    const outputString = match[2];

    super(outputString);

    this._draftTarget = cardType;
  }

  public onDraftCard(
    context: Context,
    draftedCard: CardDef,
  ): Utility.CounterDelta {
    if (draftedCard.affinity !== this._draftTarget) {
      return Utility.makeCounterDelta();
    }
    return computeAbilityOutputs(this.outputs, context);
  }

  public getDraftTarget(): DraftTarget {
    return this._draftTarget;
  }
}

export type GainTargetType =
  | {
      target_type: 'card_affinity';
      card_affinity: CardAffinity;
    }
  | {
      target_type: 'card_type';
      card_type: CardType;
    }
  | {
      target_type: 'tribute';
    }
  | {
      target_type: 'token';
      token: Token;
    };

export class GainOtherCardAbility extends BaseCardAbility {
  private _targetType: GainTargetType;

  constructor(effectString: string) {
    const pattern = /\+([DTZUDEBTW])=(.*)/;
    const result = pattern.exec(effectString);
    invariant(result, 'invalid gain card ability: %s', effectString);
    const cardChar = result[1];
    const targetType = match<string, GainTargetType>(cardChar)
      // XXX: T is Tyros for now, migrate tribute to some other char
      // .with('T', () => ({ target_type: 'tribute' }))
      .with('W', () => ({ target_type: 'token', token: Token.War }))
      .when(maybeCharToCardType, (c) => ({
        target_type: 'card_type',
        card_type: typeCharToCardType(c),
      }))
      .otherwise((c) => ({
        target_type: 'card_affinity',
        card_affinity: affinityCharToCardAffinity(c),
      }));
    const outputString = result[2];

    super(outputString);

    this._targetType = targetType;
  }
  public onGainCard(
    context: Context,
    gainedCard: CardDef,
  ): Utility.CounterDelta {
    const shouldTrigger = match(this._targetType)
      .with({ target_type: 'tribute' }, () => false)
      .with({ target_type: 'token' }, () => false)
      .with({ target_type: 'card_affinity' }, (x) => {
        return gainedCard.affinity === x.card_affinity;
      })
      .with({ target_type: 'card_type' }, (x) => {
        return gainedCard.type === x.card_type;
      })
      .exhaustive();

    if (!shouldTrigger) {
      return Utility.makeCounterDelta();
    }

    return computeAbilityOutputs(this.outputs, context);
  }
  public onGainTribute(context: Context): Utility.CounterDelta {
    if (this._targetType.target_type !== 'tribute') {
      return Utility.makeCounterDelta();
    }

    return computeAbilityOutputs(this.outputs, context);
  }
  public onGainToken(context: Context, token: Token): Utility.CounterDelta {
    if (
      this._targetType.target_type !== 'token' ||
      this._targetType.token !== token
    ) {
      return Utility.makeCounterDelta();
    }

    return computeAbilityOutputs(this.outputs, context);
  }

  getTargetType(): GainTargetType {
    return this._targetType;
  }
}

// Each Ability subclass is a pure function of its single effect string — the
// constructor parses the string and stores immutable fields. Cache instances by
// the effect string so corpus/self-play loops don't reallocate the same
// handful of abilities millions of times.
function memoizeByEffectString<T>(
  build: (effectString: string) => T,
): (effectString: string) => T {
  const cache = new Map<string, T>();
  return (effectString) => {
    const cached = cache.get(effectString);
    if (cached !== undefined) {
      return cached;
    }
    const instance = build(effectString);
    cache.set(effectString, instance);
    return instance;
  };
}

const buildBaseCountersAbility = memoizeByEffectString(
  (s) => new BaseCountersAbility(s),
);
const buildGainSelfAbility = memoizeByEffectString(
  (s) => new GainSelfAbility(s),
);
const buildEachTurnAbility = memoizeByEffectString(
  (s) => new EachTurnAbility(s),
);
const buildDraftCardAbility = memoizeByEffectString(
  (s) => new DraftCardAbility(s),
);
const buildGainOtherCardAbility = memoizeByEffectString(
  (s) => new GainOtherCardAbility(s),
);
const buildEndOfGameAbility = memoizeByEffectString(
  (s) => new EndOfGameAbility(s),
);

export function getBaseCountersAbility(
  card: CardDef,
): BaseCountersAbility | null {
  return card.baseEffect ? buildBaseCountersAbility(card.baseEffect) : null;
}

export function getGainSelfCardAbility(card: CardDef): GainSelfAbility | null {
  return card.gainSelfEffect ? buildGainSelfAbility(card.gainSelfEffect) : null;
}
export function getEachTurnCardAbility(card: CardDef): EachTurnAbility | null {
  return card.turnEffect ? buildEachTurnAbility(card.turnEffect) : null;
}
export function getDraftCardAbility(card: CardDef): DraftCardAbility | null {
  return card.draftOtherEffect
    ? buildDraftCardAbility(card.draftOtherEffect)
    : null;
}
export function getGainOtherCardAbility(
  card: CardDef,
): GainOtherCardAbility | null {
  return card.gainOtherEffect
    ? buildGainOtherCardAbility(card.gainOtherEffect)
    : null;
}
export function getEndOfGameCardAbility(
  card: CardDef,
): EndOfGameAbility | null {
  return card.endOfGameEffect
    ? buildEndOfGameAbility(card.endOfGameEffect)
    : null;
}

export function getAllCardAbilities(card: CardDef): BaseCardAbility[] {
  return [
    getEndOfGameCardAbility(card),
    getGainOtherCardAbility(card),
    getDraftCardAbility(card),
    getEachTurnCardAbility(card),
    getBaseCountersAbility(card),
  ].filter(nonNull);
}

export type CardAdditionalCosts = {
  warTokens: number;
};
export function getAdditionalCosts(card: CardDef): CardAdditionalCosts | null {
  if (!card.additionalCosts) {
    return null;
  }
  const pattern = /(\d*)([W])/;
  const match = pattern.exec(card.additionalCosts);
  if (!match) {
    return null;
  }
  return {
    warTokens: parseInt(match[1] || '1'),
  };
}

export type AbilityOutputMultiplierType =
  | CardAffinity
  | CardType
  | 'card_set'
  | Token;
export type AbilityOutputMultiplier = {
  resource: AbilityOutputMultiplierType;
  // ratio: number;
  resourceDivisor: number;
};

export function abilityUpgradeConditionToString(
  condition: AbilityUpgradeCondition,
): string {
  return `${condition.upgradeAmount}${divisorTypeToDivisorChar(condition.upgradeType)}`;
}
export type AbilityUpgradeCondition = {
  upgradeType: AbilityOutputMultiplierType;
  upgradeAmount: number;
};
export function areAbilityUpgradeConditionsEqual(
  a: AbilityUpgradeCondition | null,
  b: AbilityUpgradeCondition | null,
): boolean {
  if (a === null && b === null) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  return a.upgradeType === b.upgradeType && a.upgradeAmount === b.upgradeAmount;
}

export type AbilityOutput = {
  outputResource: Resource;
  outputRatio: number;

  multiplier?: AbilityOutputMultiplier;
  upgradeCondition?: AbilityUpgradeCondition;
};
function divisorCharToDivisorType(char: string): AbilityOutputMultiplierType {
  if (char === 'S') {
    return 'card_set';
  }
  if (char === 'W') {
    return Token.War;
  }
  const type = maybeCharToCardType(char);
  if (type) {
    return type;
  }
  const affinity = maybeCharToCardAffinity(char);
  if (affinity) {
    return affinity;
  }
  invariant(false, 'invalid divisor character: %s', char);
}

function divisorTypeToDivisorChar(type: AbilityOutputMultiplierType): string {
  if (type === 'card_set') {
    return 'S';
  }
  if (type === Token.War) {
    return 'W';
  }
  if (isCardType(type)) {
    return getCardTypeChar(type);
  }
  if (isCardAffinity(type)) {
    return getCardAffinityChar(type);
  }
  invariant(false, 'invalid divisor type: %s', type);
}

// Card effect strings are static across the process; the parse result is a pure
// function of the input. Memoize so corpus/self-play loops don't reparse the
// same handful of effect strings millions of times.
const parsedAbilityOutputsCache = new Map<string, AbilityOutput[]>();
function parseAbilityOutputs(outputs: string): AbilityOutput[] {
  const cached = parsedAbilityOutputsCache.get(outputs);
  if (cached !== undefined) {
    return cached;
  }
  const parsed = outputs.split(' ').map(parseAbilityOutput).flat();
  parsedAbilityOutputsCache.set(outputs, parsed);
  return parsed;
}

// XXX: it would be better to parse the characters more generically
// and also with error checking for invalid characters
const DIVISOR_PATTERN = `SW${getAllCardChars().join('')}`;
const RESOURCE_PATTERN = `GMV`;
const SYNERGY_PATTERN = new RegExp(
  `(\\d*)([${RESOURCE_PATTERN}])\\/(\\d*)([${DIVISOR_PATTERN}])`,
);
const FLAT_PATTERN = new RegExp(`(\\d*)([${RESOURCE_PATTERN}])`);
const UPGRADE_PATTERN = new RegExp(
  `(\\d*)([${RESOURCE_PATTERN}])>(\\d*)([${DIVISOR_PATTERN}])`,
);

function parseAbilityOutput(output: string): AbilityOutput {
  const synergyMatch = SYNERGY_PATTERN.exec(output);
  if (synergyMatch) {
    const resourceChar = synergyMatch[2];
    const resource = CharacterToResourceName[resourceChar];
    const resourceAmount = parseInt(synergyMatch[1] || '1');
    const divisorAmount = parseInt(synergyMatch[3] || '1');
    const divisorResourceChar = synergyMatch[4];
    const divisorResource = divisorCharToDivisorType(divisorResourceChar);
    return {
      outputResource: resource,
      outputRatio: resourceAmount,
      multiplier: {
        resource: divisorResource,
        resourceDivisor: divisorAmount,
      },
    };
  }

  const upgradeMatch = UPGRADE_PATTERN.exec(output);
  if (upgradeMatch) {
    const resourceChar = upgradeMatch[2];
    const resource = CharacterToResourceName[resourceChar];
    const resourceAmount = parseInt(upgradeMatch[1] || '1');
    const upgradeAmount = parseInt(upgradeMatch[3] || '1');
    const upgradeTypeChar = upgradeMatch[4];
    const upgradeType = divisorCharToDivisorType(upgradeTypeChar);
    return {
      outputResource: resource,
      outputRatio: resourceAmount,
      upgradeCondition: {
        upgradeType: upgradeType,
        upgradeAmount: upgradeAmount,
      },
    };
  }

  const match = FLAT_PATTERN.exec(output);
  if (match) {
    const resourceChar = match[2];
    const resource = CharacterToResourceName[resourceChar];
    const resourceAmount = parseInt(match[1] || '1');
    return {
      outputResource: resource,
      outputRatio: resourceAmount,
    };
  }

  invariant(false, 'unable to parse ability output: %s', output);
}

function computeAbilityOutputs(
  abilityOutputs: AbilityOutput[],
  context: Context,
): Utility.CounterDelta {
  return abilityOutputs.reduce((output, abilityOutput) => {
    const delta = computeAbilityOutput(abilityOutput, context);
    return Utility.applyCounterDelta(output, delta);
  }, Utility.makeCounterDelta());
}

function computeAbilityOutput(
  abilityOutput: AbilityOutput,
  context: Context,
): Utility.CounterDelta {
  const output = Utility.makeCounterDelta();

  const upgradeCondition = abilityOutput.upgradeCondition;
  if (upgradeCondition && !upgradeConditionMet(upgradeCondition, context)) {
    return output;
  }

  const outputResource = abilityOutput.outputResource;
  const outputRatio = abilityOutput.outputRatio;

  let multiplier = 1;
  if (abilityOutput.multiplier) {
    const base = computeOutputMultiplierForPlayer(
      abilityOutput.multiplier.resource,
      context,
    );
    multiplier = Math.floor(base / abilityOutput.multiplier.resourceDivisor);
  }

  output[outputResource] = outputRatio * multiplier;

  return output;
}

export function upgradeConditionMet(
  condition: AbilityUpgradeCondition,
  context: Context,
): boolean {
  const base = computeOutputMultiplierForPlayer(condition.upgradeType, context);
  return base >= condition.upgradeAmount;
}

export function computeOutputMultiplierForPlayer(
  multiplierType: AbilityOutputMultiplierType,
  context: Context,
): number {
  return match(multiplierType)
    .with('card_set', () => Utility.countBoardSets(context.player.cards))
    .with(Token.War, () => context.player.counters.warTokens)
    .when(isCardType, (cardType) =>
      countCardsOfType(context.player.cards, cardType),
    )
    .when(isCardAffinity, (cardAffinity) =>
      countCardsOfAffinity(context.player.cards, cardAffinity),
    )
    .otherwise((x) => {
      invariant(false, 'invalid multiplier resource: %s', x);
    });
}

function countCardsOfAffinity(
  cards: CardDef[],
  affinity: CardAffinity,
): number {
  return Utility.countIf(cards, (card) => card.affinity === affinity);
}

function countCardsOfType(cards: CardDef[], type: CardType): number {
  return Utility.countIf(cards, (card) => card.type === type);
}
